import * as constants from '@/ts/utils/consts';
import {createApp} from 'vue';
import {Quasar} from 'quasar';
import type {LogMethods} from '@significo/pulse';
import type {App as VueApp} from '@vue/runtime-core';
import {addDirectives} from '@/ts/directives';
import {routerFactory} from '@/ts/router/router-factory';
import {loggerInstance} from '@/ts/instances/logger-instance';
import {createPinia} from 'pinia';
import {i18nFactory} from '@/ts/instances/i18n';
import {getQuasarOptions} from '@/ts/instances/quasar-options';
import {sentryService} from '@/ts/services/sentry-service';
import {analyticsService} from '@/ts/services/analytics-service';
import {initAutoHideScrollbars} from '@/ts/utils/auto-hide-scrollbars';
import {initBfcacheAuthGuard} from '@/ts/utils/bfcache-auth-guard';
import {unauthenticatedRedirectOf} from '@/ts/router/unauthenticated-redirect';
import App from '@/vue/app.vue';

// eslint-disable-next-line import-x/no-unassigned-import
import '@/assets/sass/global-css/index.scss';
// eslint-disable-next-line import-x/no-unassigned-import
import 'bigger-picture/css';

const logger: LogMethods = loggerInstance.getLogger('main');
logger.info(`Evaluating main script ${constants.GIT_HASH}`)();

// eslint-disable-next-line, max-statements
function init(): void {
  // Hotfix for Edge 15 for reflect data
  if (!window.InputEvent) {
    // @ts-expect-error: next-line
    window.InputEvent = (): void => {
    };
  }

  const pinia = createPinia();
  const router = routerFactory();
  const app: VueApp = addDirectives(createApp(App))
    .use(router)
    .use(pinia)
    .use(Quasar, getQuasarOptions())
    .use(i18nFactory());
  analyticsService.start('main');

  // Initialize Sentry BEFORE mounting the app (captures Vue errors automatically)
  sentryService.initializeSentry(app, router);

  // Vue error handler - also reports to Sentry
  app.config.errorHandler = (err, vm, info): boolean => {
    logger.error('Error occurred in vue component err:', err, 'vm:', vm, 'info:', info)();
    // captureAlways, not captureException: reaching Vue's errorHandler means the render path broke,
    // and a blanked page must stay visible in Sentry even when the underlying code is a business one.
    sentryService.captureAlways(err as Error, {
      component: vm?.$options?.name ?? 'unknown',
      info,
    });
    return false;
  };

  app.mount(document.querySelector('#q-app'));
  initAutoHideScrollbars();
  initBfcacheAuthGuard(router, unauthenticatedRedirectOf(router));

  // Global error handler - also reports to Sentry
  // eslint-disable-next-line @typescript-eslint/max-params
  window.onerror = function onerror(msg, url, linenumber, colno, error): boolean {
    const message = `Error occurred in ${url!}:${linenumber!}\n${JSON.stringify(msg)}`;
    logger.error(message)();
    if (error) {
      sentryService.captureException(error, {
        url,
        line: linenumber,
        column: colno,
      });
    }
    return false;
  };

  // Unhandled promise rejections - also reports to Sentry
  window.onunhandledrejection = function onunhandledrejection(event): void {
    logger.error('Unhandled promise rejection:', event.reason)();
    sentryService.captureException(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {type: 'unhandledrejection'},
    );
  };

  window.gitVersion = constants.GIT_HASH;
  if (constants.IS_DEBUG) {
    window.vue = app;
    window.consts = constants;
    window.pinia = pinia;
    logger.info('Constants', constants)();
  }
}

if (document.body) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init);
}
